'use client';

import { Check } from 'lucide-react';
import { useLocation, useNavigate } from 'react-router-dom';
import { locales, type Locale, localeLabels } from '@/lib/i18n/config';
import { getDictionary } from '@/lib/i18n/dictionaries';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Button } from '@/components/ui/button';
import { FlagIcon } from './flag-icon';

export function LocaleSwitcher({ current, inverse = false }: { current: Locale; inverse?: boolean }) {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const dict = getDictionary(current);

  function swap(locale: Locale) {
    if (locale === current) return;
    const parts = (pathname || '/').split('/');
    if (parts[1] && (locales as readonly string[]).includes(parts[1])) {
      parts[1] = locale;
    } else {
      parts.splice(1, 0, locale);
    }
    const next = parts.join('/') || `/${locale}`;
    navigate(next);
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          aria-label={dict.a11y.selectLanguage}
          className={inverse ? 'gap-1.5 text-white hover:bg-white/10 hover:text-white' : 'gap-1.5'}
        >
          <FlagIcon locale={current} />
          <span className="text-xs font-semibold uppercase">{current}</span>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-[12rem]">
        {locales.map((l) => {
          const active = l === current;
          return (
            <DropdownMenuItem
              key={l}
              onClick={() => swap(l)}
              lang={l}
              aria-current={active ? 'true' : undefined}
              className="cursor-pointer gap-2.5"
            >
              <FlagIcon locale={l} />
              <span className="w-6 text-xs font-semibold uppercase text-muted-foreground">{l}</span>
              <span className="flex-1">{localeLabels[l]}</span>
              {active && <Check className="h-4 w-4 text-accent" aria-hidden="true" />}
            </DropdownMenuItem>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
